import {useQuery, UseQueryResult} from '@tanstack/react-query';
import {useEffect, useMemo} from 'react';

import {usePagination} from '../../../util/pagination-hook/pagination-hook';
import {PaginationOptionsType, PaginationType} from '../../../util/pagination-hook/pagination-hook-type';
import {PaginatedResponseType} from '../../api/api-type';
import {useApiHooks} from '../../api-hook/api-hook';
import {UseHookType} from '../../api-hook/api-hook-type';

import {fetchReviewCityList, getCompetitorCities} from './review-cities-api';
import {ReviewCityListQueryType, ReviewCityType, ReviewCompetitorCityType} from './review-cities-type';

export function useReviewCityList(
    options: ReviewCityListQueryType,
    sortDirection: string | null,
    mainFilterKey: string,
    paginationOption?: PaginationOptionsType
): UseHookType<PaginatedResponseType<ReviewCityType>> & PaginationType {
    const {isInProgress, setIsInProgress, processError, setProcessError, result, setResult, reset} =
        useApiHooks<PaginatedResponseType<ReviewCityType>>();

    const {
        startDate,
        endDate,
        sentiments,
        sources,
        tags,
        phrases,
        topics,
        rating,
        withAnswers,
        withText,
        ableToReply,
        useDeleted,
        target,
        userTagsIds,
        aspectIds,
        mode,
        brandIds: competitorBrandIds,
    } = options;

    const paginationDependencies = useMemo(() => {
        return {
            startDate,
            endDate,
            sentiments,
            sources,
            tags,
            phrases,
            topics,
            rating,
            withAnswers,
            withText,
            ableToReply,
            mainFilterKey,
            target,
            useDeleted,
            userTagsIds,
            aspectIds,
            mode,
            competitorBrandIds,
            sortDirection,
        };
    }, [
        startDate,
        endDate,
        sentiments,
        sources,
        tags,
        phrases,
        topics,
        rating,
        withAnswers,
        withText,
        ableToReply,
        target,
        useDeleted,
        mainFilterKey,
        userTagsIds,
        aspectIds,
        mode,
        competitorBrandIds,
        sortDirection,
    ]);

    const pagination = usePagination({...paginationOption, dependencies: paginationDependencies});

    const {page, pageSize, onDataLoadFailed, onDataLoaded, refreshId} = pagination;

    useEffect(() => {
        setIsInProgress(true);
        setProcessError(null);

        fetchReviewCityList(
            {
                page,
                pageSize,
                startDate,
                endDate,
                sentiments,
                sources,
                tags,
                phrases,
                topics,
                rating,
                withAnswers,
                withText,
                ableToReply,
                target,
                useDeleted,
                userTagsIds,
                aspectIds,
                mode,
                brandIds: competitorBrandIds,
            },
            mainFilterKey,
            sortDirection || ''
        )
            .then((response) => {
                onDataLoaded(response);
                setResult(response);
            })
            .finally(() => setIsInProgress(false))
            .catch((error) => {
                onDataLoadFailed();
                setProcessError(error);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshId, sortDirection, setIsInProgress, setProcessError, setResult, onDataLoaded, onDataLoadFailed]);

    return {isInProgress, processError, result, reset, pagination};
}

export function useReviewCompetitorCities(
    options: ReviewCityListQueryType,
    mainFilterKey: string,
    paginationOption?: PaginationOptionsType
): UseQueryResult<PaginatedResponseType<ReviewCompetitorCityType>> & PaginationType {
    const {
        startDate,
        endDate,
        sentiments,
        sources,
        tags,
        phrases,
        topics,
        rating,
        withAnswers,
        withText,
        ableToReply,
        useDeleted,
        target,
        userTagsIds,
        aspectIds,
        mode,
        brandIds: competitorBrandIds,
    } = options;

    const paginationDependencies = useMemo(() => {
        return {
            startDate,
            endDate,
            sentiments,
            sources,
            tags,
            phrases,
            topics,
            rating,
            withAnswers,
            withText,
            ableToReply,
            mainFilterKey,
            target,
            useDeleted,
            userTagsIds,
            aspectIds,
            mode,
            competitorBrandIds,
        };
    }, [
        startDate,
        endDate,
        sentiments,
        sources,
        tags,
        phrases,
        topics,
        rating,
        withAnswers,
        withText,
        ableToReply,
        target,
        useDeleted,
        mainFilterKey,
        userTagsIds,
        aspectIds,
        mode,
        competitorBrandIds,
    ]);

    const pagination = usePagination({
        ...paginationOption,
        dependencies: paginationDependencies,
    });

    const {page, pageSize, onDataLoadFailed, onDataLoaded} = pagination;

    const query = useQuery(
        ['competitor-cities', page, pageSize, options, mainFilterKey],
        () => getCompetitorCities({page, pageSize, ...options}, mainFilterKey),
        {
            onSuccess: (data) => onDataLoaded(data),
            onError: onDataLoadFailed,
        }
    );

    return {
        ...query,
        pagination,
    };
}
